import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Game, Player, GroupCode, Goal, Penalty, Intervention } from "../../types";
import { useYearContext } from "../../hooks/useYearContext";
import { format } from "date-fns";
import { sk } from "date-fns/locale";

interface PlayerWithStats extends Player {
    goals: number;
    assists: number;
    penalties: number;
    interventions: number;
    gotGoals: number;
    team_id: number;
}

interface TeamInfoProps {
    status: string;
    team: string | undefined;
    goals?: number;
    code?: GroupCode;
}

interface ShowGoalsProps {
    players: PlayerWithStats[];
    status: string;
}

interface ShowKeeperProps {
    players: PlayerWithStats[];
    status: string;
    gk: number | undefined;
}

const UserGameDetail = () => {
    const { current } = useYearContext();
    const { id } = useParams<{ id: string }>();
    const [game, setGame] = useState<Game | undefined>();
    const [players, setPlayers] = useState<PlayerWithStats[]>([]);
    const [gk_a, setgk_a] = useState<number | undefined>();
    const [gk_b, setgk_b] = useState<number | undefined>();
    const [codes, setCodes] = useState<GroupCode[]>([]);

    useEffect(() => {
        const getGame = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/game/${id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: Game = await response.json();

                if (response.ok) {
                    setGame(json);
                }
            } catch (error) {
                console.error('Error fetching game:', error);
            }
        };

        const getGroupCodes = async () => {
            if (!current) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/group/codes?year=${current.year}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: GroupCode[] = await response.json();

                if (response.ok) {
                    setCodes(json);
                }
            } catch (error) {
                console.error('Error fetching group codes:', error);
            }
        };

        getGame();
        getGroupCodes();
    }, [id, current]);

    useEffect(() => {
        if (!game) return;

        const getPlayers = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/player/match?t1=${game.team_a_id}&t2=${game.team_b_id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: Player[] = await response.json();

                if (response.ok) {
                    const playersWithStats: PlayerWithStats[] = json.map(p => ({
                        ...p,
                        goals: 0,
                        assists: 0,
                        penalties: 0,
                        interventions: 0,
                        gotGoals: 0
                    }));
                    setPlayers(playersWithStats);
                    
                    const keeper_a = playersWithStats.find(x => x.team_id === game.team_a_id && x.role === 'gk');
                    const keeper_b = playersWithStats.find(x => x.team_id === game.team_b_id && x.role === 'gk');
                    setgk_a(keeper_a?.ID);
                    setgk_b(keeper_b?.ID);
                }
            } catch (error) {
                console.error('Error fetching players:', error);
            }
        };

        getPlayers();
    }, [game]);

    useEffect(() => {
        if (!game || players.length === 0) return;

        const getGameStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/stats/game/${id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: { goals: Goal[], penalties: Penalty[], interventions: Intervention[] } = await response.json();

                if (response.ok) {
                    const updatedPlayers = [...players];

                    json.goals.forEach(e => {
                        const shooter = updatedPlayers.find(x => x.ID === e.shooter);
                        if (shooter) shooter.goals++;

                        if (e.assist) {
                            const assister = updatedPlayers.find(x => x.ID === e.assist);
                            if (assister) assister.assists++;
                        }

                        if (e.keeper) {
                            const keeper = updatedPlayers.find(x => x.ID === e.keeper);
                            if (keeper) keeper.gotGoals++;
                        }
                    });

                    json.penalties.forEach(e => {
                        const player = updatedPlayers.find(x => x.ID === e.player);
                        if (player) player.penalties += e.min;
                    });

                    json.interventions.forEach(e => {
                        const keeper = updatedPlayers.find(x => x.ID === e.player);
                        if (keeper) keeper.interventions += e.count;
                    });

                    setPlayers(updatedPlayers);
                }
            } catch (error) {
                console.error('Error fetching game stats:', error);
            }
        };

        getGameStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game, id, players.length]); // Only depend on length to avoid infinite loop

    const TeamInfo: React.FC<TeamInfoProps> = ({ status, team, goals, code }) => (
        <div className="w-full md:w-1/2 flex flex-col items-center text-center my-6">
            {code && <p className="text-sm text-gray-500">{code.name}</p>}
            <h2 className="text-4xl font-bold mb-2">{team}</h2>
            {status !== "scheduled" && <p className="text-6xl font-bold text-accent">{goals}</p>}
        </div>
    );

    const ShowGoals: React.FC<ShowGoalsProps> = ({ players, status }) => {
        if (status === "scheduled") return null;
        
        const scoringPlayers = players.filter(x => x.goals > 0);
        if (scoringPlayers.length === 0) return null;

        return (
            <div className="w-full my-4">
                <h3 className="text-xl font-semibold mb-2">Góly:</h3>
                {scoringPlayers.map((player, i) => (
                    <div key={i} className="flex items-center mb-1">
                        <span className="text-lg">
                            {player.first_name} {player.last_name}
                            {player.goals > 1 && ` (${player.goals})`}
                            {player.assists > 0 && ` - ${player.assists}A`}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const ShowKeeper: React.FC<ShowKeeperProps> = ({ players, status, gk }) => {
        if (status === "scheduled" || !gk) return null;

        const keeper = players.find(x => x.ID === gk);
        if (!keeper) return null;

        const { gotGoals, interventions } = keeper;
        const total = gotGoals + interventions;
        if (total === 0) return null;

        const percentage = Math.round((100 - (gotGoals / total * 100)));

        return (
            <div className="w-full my-4">
                <h3 className="text-xl font-semibold mb-2">Brankár:</h3>
                <div className="flex flex-col">
                    <span className="text-lg mb-2">
                        {keeper.first_name} {keeper.last_name}
                    </span>
                    <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div 
                            className="bg-accent h-full flex items-center justify-center text-white font-semibold transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                        >
                            {percentage > 20 && `${percentage}%`}
                        </div>
                    </div>
                    <span className="text-sm text-gray-600 mt-1">
                        {interventions} zákrokov, {gotGoals} gólov
                    </span>
                </div>
            </div>
        );
    };

    if (!game) return <div className="text-center mt-12">Loading...</div>;

    const team_a = game.team_a_name;
    const team_b = game.team_b_name;
    const code_a = codes.find(c => c.ID === game.code_a);
    const code_b = codes.find(c => c.ID === game.code_b);

    const getGameTypeLabel = () => {
        if (game.text === "group") return "Skupinový zápas";
        if (game.text === "o3") return "O 3. miesto";
        if (game.text === "o5") return "O 5. miesto";
        if (game.text === "o7") return "O 7. miesto";
        if (game.text === "QF1" || game.text === "QF2" || game.text === "QF3" || game.text === "QF4") return "Štvrťfinále";
        if (game.text === "SF1" || game.text === "SF2") return "Semifinále";
        if (game.text === "Final") return "Finále";
        return "";
    };

    return (
        <div className="w-[90vw] md:w-[70vw] xl:w-[52vw] mx-auto mt-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
                {/* Game Type */}
                {game.text !== "group" && (
                    <div className="text-center mb-4">
                        <span className="inline-block bg-accent text-white px-4 py-2 rounded-full font-semibold">
                            {getGameTypeLabel()}
                        </span>
                    </div>
                )}

                {/* Live Status */}
                {game.status === "live" && (
                    <div className="flex items-center justify-center mb-4">
                        <span className="relative flex h-3 w-3 mr-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
                        </span>
                        <span className="text-accent font-semibold">LIVE</span>
                    </div>
                )}

                {/* Game Date and Time */}
                <div className="text-center text-gray-600 mb-4">
                    <p>{format(new Date(game.time), "EEEE, d. MMMM yyyy", { locale: sk }).charAt(0).toUpperCase() + format(new Date(game.time), "EEEE, d. MMMM yyyy", { locale: sk }).slice(1)}</p>
                    <p>{game.time.substring(game.time.indexOf("T") + 1, game.time.indexOf("T") + 6)}</p>
                    {game.field_name && <p className="font-semibold">{game.field_name}</p>}
                </div>

                {/* Teams */}
                <div className="flex flex-col md:flex-row justify-around items-center border-t border-b border-gray-200 py-6">
                    <TeamInfo status={game.status} team={team_a} goals={game.team_a_goals} code={code_a} />
                    <div className="text-4xl font-bold text-gray-400 mx-4">VS</div>
                    <TeamInfo status={game.status} team={team_b} goals={game.team_b_goals} code={code_b} />
                </div>

                {/* Team A Stats */}
                {team_a && (
                    <div className="mt-6 p-4 bg-gray-50 rounded">
                        <h2 className="text-2xl font-bold mb-4 text-center">{team_a}</h2>
                        <ShowGoals players={players.filter(p => p.team_id === game.team_a_id)} status={game.status} />
                        <ShowKeeper players={players.filter(p => p.team_id === game.team_a_id)} status={game.status} gk={gk_a} />
                    </div>
                )}

                {/* Team B Stats */}
                {team_b && (
                    <div className="mt-6 p-4 bg-gray-50 rounded">
                        <h2 className="text-2xl font-bold mb-4 text-center">{team_b}</h2>
                        <ShowGoals players={players.filter(p => p.team_id === game.team_b_id)} status={game.status} />
                        <ShowKeeper players={players.filter(p => p.team_id === game.team_b_id)} status={game.status} gk={gk_b} />
                    </div>
                )}

                {/* Best Player */}
                {game.status === "finished" && game.best_player && (
                    <div className="mt-6 text-center p-4 bg-accent bg-opacity-10 rounded">
                        <p className="text-lg font-semibold">Najlepší hráč zápasu:</p>
                        <p className="text-xl text-accent font-bold">
                            {players.find(p => p.ID === game.best_player) ? `${players.find(p => p.ID === game.best_player)?.first_name} ${players.find(p => p.ID === game.best_player)?.last_name}` : ''}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserGameDetail;
